import i18n from '@/i18n';
import { bcp47Of } from '@/i18n/languages';
import { t } from '@/i18n/t';

/**
 * Sprachabhängige Datums-, Zeit- und Zahlenformate.
 *
 * Ersetzt die verstreuten `toLocaleDateString('de-DE')`-Aufrufe.
 * Für Deutsch ist die Ausgabe IDENTISCH: nach ECMA-402 gilt
 *   date.toLocaleDateString(loc, opts) === new Intl.DateTimeFormat(loc, opts).format(date)
 */

const currentBcp47 = (): string => bcp47Of(i18n.language || 'de');

const fmtCache = new Map<string, Intl.DateTimeFormat>();
const dtf = (opts: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat => {
  const loc = currentBcp47();
  const key = loc + JSON.stringify(opts);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(loc, opts);
    fmtCache.set(key, f);
  }
  return f;
};

type DateInput = string | number | Date;
const asDate = (v: DateInput): Date => (v instanceof Date ? v : new Date(v));
const valid = (d: Date): boolean => !isNaN(d.getTime());

/** entspricht toLocaleDateString(loc) — nur Datum, Standardformat */
export const formatDate = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d) ? dtf().format(d) : '';
};

/** TT.MM */
export const formatDateShort = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d) ? dtf({ day: '2-digit', month: '2-digit' }).format(d) : '';
};

/** TT.MM.JJJJ */
export const formatDateLong = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d) ? dtf({ day: '2-digit', month: '2-digit', year: 'numeric' }).format(d) : '';
};

/** TT.MM.JJJJ, HH:MM */
export const formatDateTime = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d)
    ? dtf({ day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d)
    : '';
};

/** HH:MM */
export const formatTime = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d) ? dtf({ hour: '2-digit', minute: '2-digit' }).format(d) : '';
};

/** TT.MM, HH:MM (ohne Jahr) */
export const formatDayTime = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d)
    ? dtf({ day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d)
    : '';
};

/** entspricht toLocaleString(loc) ohne Optionen — Datum UND Uhrzeit inkl. Sekunden */
export const formatDateTimeFull = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d)
    ? dtf({
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric',
      }).format(d)
    : '';
};

/** Freie Formatierung mit eigenen Intl-Optionen (für Sonderfälle). */
export const formatCustom = (v: DateInput | null | undefined, opts: Intl.DateTimeFormatOptions): string => {
  if (!v) return '';
  const d = asDate(v);
  return valid(d) ? dtf(opts).format(d) : '';
};

export const formatNumber = (n: number, opts?: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat(currentBcp47(), opts).format(n);

export const formatCurrency = (n: number, currency = 'EUR'): string =>
  formatNumber(n, { style: 'currency', currency });

/**
 * Relative Zeit ("vor 45 Min.").
 *
 * Bewusst NICHT Intl.RelativeTimeFormat: das liefert "vor 45 Minuten" und würde
 * die bestehende deutsche Ausgabe verändern. Über Plural-Schlüssel bleibt
 * Deutsch identisch, während andere Sprachen korrekt pluralisiert werden.
 */
export const formatRelativeTime = (v?: DateInput | null): string => {
  if (!v) return '';
  const d = asDate(v);
  if (!valid(d)) return '';
  const ms = Date.now() - d.getTime();
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);

  if (hours < 1) return t('common:relative.minutes', 'vor {{count}} Min.', { count: minutes });
  if (hours < 24) return t('common:relative.hours', 'vor {{count}} Stunden', { count: hours });
  return t('common:relative.days', 'vor {{count}} Tagen', { count: days });
};
